import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Box } from "@twilio-paste/core/box";
import { Spinner } from "@twilio-paste/core/spinner";
import { Toaster, useToaster } from "@twilio-paste/core/toast";
import { useTransition, animated } from "@twilio-paste/core/animation-library";
import { ChatLog, EndedChat, WaitingInLine } from "../../components";
import useLiveChat from "../../hooks/useLiveChat";
import { getAttribute } from "../../utils";
import {
  LiveChatPhaseEnum,
  LiveChatPhaseEnumType,
  LiveChatProps,
  WiseOwlGetChatSummaryByIdResponseData,
} from "@twiliointernal/e2-commons-shared-definitions";
import { TransferToQueueProps } from "../../components/waitingInLine/definitions";
import { copy } from "./definitions/copy";
import { chatService } from "../../services";

const LiveChat = ({
  identity,
  liveChatId,
  token,
  ticketId,
  chatActivitySyncMapId,
  chatId,
  waitingActions,
  endedActions,
  appContainerId,
  handleGetChatSummary,
  ingress,
  wiseOwlToken,
  user,
}: LiveChatProps): ReactElement => {
  const {
    agentEvents,
    agent,
    liveChatAttributes,
    customer,
    dateCreated,
    isLiveChatReady,
    isAgentTyping,
    messages,
    sdkError,
    clearError,
  } = useLiveChat({ identity, liveChatId, token });
  const toaster = useToaster();

  const isLoading = !isLiveChatReady || !customer || !dateCreated;

  const [currentChatPhase, setCurrentChatPhase] =
    useState<LiveChatPhaseEnumType | null>(null);

  const [chatSummary, setChatSummary] =
    useState<WiseOwlGetChatSummaryByIdResponseData>();
  const directLiveChatTag = "direct_live_chat";

  useEffect(() => {
    chatService.initHelpCenterApi({ ingress, token: wiseOwlToken, user });
  }, [ingress, wiseOwlToken, user]);

  useEffect(() => {
    if (!liveChatAttributes) {
      return;
    }

    const chatPhase = getAttribute({
      attributes: liveChatAttributes,
      key: "chatPhase",
      fallbackValue: LiveChatPhaseEnum.Waiting,
    });

    chatPhase && setCurrentChatPhase(chatPhase);
  }, [liveChatAttributes]);

  useEffect(() => {
    // Make sure render only one time the error until is dismissed
    if (sdkError && toaster.toasts.length === 0) {
      toaster.push({
        message: sdkError,
        variant: "error",
        onDismiss: clearError,
      });
    }
  }, [sdkError, toaster]);

  const memoizedHandleGetChatSummary = useCallback(() => {
    if (handleGetChatSummary) {
      const fetchAndSetChatSummary = async () => {
        const summary = await handleGetChatSummary();
        setChatSummary(summary);
      };
      fetchAndSetChatSummary();
    }
  }, [handleGetChatSummary, setChatSummary]);

  useEffect(() => {
    memoizedHandleGetChatSummary();
  }, []);

  const renderError = (): ReactNode => {
    if (sdkError) {
      return <Toaster left={["space40", "unset", "unset"]} {...toaster} />;
    }

    return null;
  };

  const chatAnimation = useTransition(
    currentChatPhase === LiveChatPhaseEnum.Active,
    {
      from: { opacity: 1, transform: "translateY(0%)" },
      enter: { opacity: 1, transform: "translateY(0%)" },
      leave: { opacity: 0, transform: "translateY(-25%)" },
    }
  );

  const waitingAnimation = useTransition(
    currentChatPhase === LiveChatPhaseEnum.Waiting,
    {
      from: { opacity: 0, transform: "translateY(50%)" },
      enter: { opacity: 1, transform: "translateY(0%)" },
      config: { duration: 800 },
    }
  );

  const renderLoader = (): ReactNode => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center">
          <Spinner decorative={false} title="Loading" size="sizeIcon80" />
        </Box>
      );
    }

    return null;
  };

  const preEngagementData = getAttribute({
    attributes: liveChatAttributes,
    key: "pre_engagement_data",
    fallbackValue: {},
  });

  const metadata = getAttribute({
    attributes: preEngagementData,
    key: "metadata",
    fallbackValue: "{}",
  });

  const parsedMetadata = JSON.parse(decodeURIComponent(metadata));

  const renderEndChat = (): ReactNode => {
    if (currentChatPhase !== LiveChatPhaseEnum.Ended) {
      return null;
    }

    // @TODO: check and use parsedMetadata.hcaCsatProps to help render
    // for future CsatSurvey component
    return <EndedChat ticketId={ticketId} actions={endedActions} />;
  };

  const renderChatLog = (): ReactNode => {
    if (!customer || !dateCreated) {
      return null;
    }

    const participantFriendlyNames = getAttribute({
      attributes: liveChatAttributes,
      key: "participantFriendlyNames",
      fallbackValue: {},
    });

    return chatAnimation((styles, showAnimation) =>
      showAnimation ? (
        <animated.div className={"animated-chat-log"} style={styles}>
          <ChatLog
            agent={agent}
            agentEvents={agentEvents}
            chatMessages={messages}
            customer={customer}
            dateCreated={dateCreated}
            isAgentTyping={isAgentTyping}
            participantFriendlyNames={participantFriendlyNames}
            appContainerId={appContainerId}
          />
        </animated.div>
      ) : null
    );
  };

  const renderWaiting = (): ReactNode => {
    if (isLoading) {
      return null;
    }

    const transfer = getAttribute({
      attributes: liveChatAttributes || {},
      key: "transfer",
      fallbackValue: null,
    }) as unknown as TransferToQueueProps;

    const { tags }: { tags: string[] } = parsedMetadata;

    if (waitingActions?.ticketSubmission) {
      const submitTicketParams = new URLSearchParams({
        subject: tags?.includes(directLiveChatTag)
          ? copy.submitTicket.subject
          : (chatSummary?.subject as string),
        description: tags?.includes(directLiveChatTag)
          ? preEngagementData.question
          : (chatSummary?.description as string),
        chatId: chatId || "",
        product: preEngagementData.productCategory,
      }).toString();

      waitingActions.ticketSubmission.params = submitTicketParams;
    }

    return waitingAnimation((styles, showAnimation) =>
      showAnimation ? (
        <animated.div className={"animated-waiting"} style={{ ...styles }}>
          <WaitingInLine
            liveChatId={liveChatId}
            token={token}
            chatActivitySyncMapId={chatActivitySyncMapId}
            transfer={transfer}
            actions={waitingActions}
            ingress={ingress}
          />
        </animated.div>
      ) : null
    );
  };

  return (
    <Box>
      {renderError()}
      {renderLoader()}
      {renderWaiting()}
      {renderEndChat()}
      {renderChatLog()}
    </Box>
  );
};

export default LiveChat;
